import * as React from "react";
import Stripe from "stripe";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import {
  SetupIntent,
  StripeCardNumberElementOptions
} from "@stripe/stripe-js";
import { useMappedState } from "redux-react-hook";

import { theme } from "src/library/utils/theme";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import {
  CheckboxStyled,
  FormRow,
  Input,
  Select
} from "src/library/components/Forms";
import { usePaymentApi } from "src/modules/payment/api/payment.api";
import { countryList } from "src/utils/constants";
import { useDropsApi } from "src/core/drops/api/drops.api";
import { IStore } from "src/core/shared/store";
import { HelperText } from "src/library/components/HelperText/HelperText";

export interface StripeElementsSetupCardFormProps {
  onSetupIntentSuccess: (setupIntent: SetupIntent, defaultCard: boolean) => void;
}

interface StripeElementsSetupCardFormAttributes {
  name: string;
  country: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  county?: string;
  postcode: string;
  allowOffSessionPayments?: boolean;
  setAsDefaultCard?: boolean;
}

/**
 * Stripe Elements setup card form
 */
const StripeElementsSetupCardForm: React.ReactFCWithChildren<StripeElementsSetupCardFormProps> = props => {
  const stripe = useStripe();
  const elements = useElements();

  const {
    handleSubmit, register, errors
  } = useForm<StripeElementsSetupCardFormAttributes>({
    defaultValues: {
      allowOffSessionPayments: true,
      setAsDefaultCard: false
    }
  });

  const [ loading, setLoading ] = React.useState(false);
  const [ confirmCardSetupLoading, setConfirmCardSetupLoading ] = React.useState(false);
  const [ setupIntent, setSetupIntent ] = React.useState<Stripe.Response<Stripe.SetupIntent>>();
  const [ createStripeSetupIntentRes, createStripeSetupIntentReq ] = usePaymentApi("CREATE_STRIPE_SETUP_INTENT");
  const [ getUserDropsResponse, listUserDrops ] = useDropsApi("GET_USER_DROPS");

  const {
    currentDrop, currentUser, terminology
  } = useMappedState(({ drops, global }: IStore) => ({
    currentDrop: drops.drop,
    currentUser: global.user,
    terminology: global.settings?.terminology
  }));

  const totalDrops = React.useMemo(() => {
    if (getUserDropsResponse.data) {
      return getUserDropsResponse.data.count;
    }

    return;
  }, [ getUserDropsResponse.data ]);

  React.useEffect(() => {
    // grab all drops so we can determine symantics
    if (currentDrop && currentUser && !getUserDropsResponse.data) {
      listUserDrops({
        userId: currentUser.id,
        filters: {
          customerId: currentUser.customerId,
          status: "active"
        },
        pagination: {
          offset: 0,
          page: 0,
          limit: 1000,
          sort: [ [ "name", "ASC" ] ]
        }
      });
    }
  }, [
    currentDrop,
    currentUser,
    getUserDropsResponse.data,
    listUserDrops
  ]);

  const stripeElementOptions = React.useMemo((): StripeCardNumberElementOptions => {
    return {
      style: {
        base: {
          "padding": "1rem 1.5rem",
          "color": theme.fonts.colors.primary,
          "backgroundColor": "transparent",
          "fontSize": "1.2rem",
          "fontFamily": "Source Code Pro, monospace",
          "::placeholder": { color: theme.fonts.colors.secondary }
        },
        invalid: { color: theme.fonts.colors.error }
      }
    };
  }, []);

  /**
   * Handles the form submission
   */
  const submitForm = React.useCallback((values: StripeElementsSetupCardFormAttributes) => {
    const cardElement = elements ? elements.getElement(CardNumberElement) : null;

    // Ensure that both stripe and its elements have loaded before we continue
    if (!(cardElement && stripe && elements && setupIntent && setupIntent.client_secret)) {
      return;
    }

    // Upload the confirmCardSetupLoading state
    setConfirmCardSetupLoading(true);

    // Attempt to create the card in Stripe using the given setupIntent.client_secret
    stripe
      .confirmCardSetup(setupIntent.client_secret as string, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: values.name,
            address: {
              country: values.country,
              line1: values.addressLine1,
              line2: values.addressLine2,
              city: values.city,
              postal_code: values.postcode
            }
          }
        }
      })
      .then(({ error, setupIntent }) => {
        // If an error occurs throw it
        if (error) {
          throw error;
        }

        // Error if there is no setupIntent data
        if (!setupIntent) {
          throw new Error("No card data was returned by the payment gateway");
        }

        // Upload the confirmCardSetupLoading state
        setConfirmCardSetupLoading(false);
        // Call the onSetupIntentSuccess function
        props.onSetupIntentSuccess(setupIntent, values.setAsDefaultCard || false);
      }).catch(err => {
        // Upload the confirmCardSetupLoading state
        setConfirmCardSetupLoading(false);
        // Show the error
        toast.error(err.message);
      });
  }, [
    elements,
    props,
    setupIntent,
    stripe
  ]);

  // On mount
  React.useEffect(() => {
    // Create a Stripe SetupIntent via the API
    // todo: set based on checkbox option
    createStripeSetupIntentReq({ sessionType: "off_session" });
  }, [ createStripeSetupIntentReq ]);

  // On createStripeSetupIntentRes change
  React.useEffect(() => {
    // If an error occurs
    if (createStripeSetupIntentRes.error) {
      toast.error(createStripeSetupIntentRes.error.message);
    }

    // On success, update the setupIntent state
    if (createStripeSetupIntentRes.data) {
      setSetupIntent(createStripeSetupIntentRes.data);
    }
  }, [ createStripeSetupIntentRes.data, createStripeSetupIntentRes.error ]);

  // On stripe and elements change
  React.useEffect(() => {
    // Update the loading state
    setLoading(!stripe || !elements);
  }, [ elements, stripe ]);

  return (
    <StyledStripeElementsSetupCardForm>
      {loading || createStripeSetupIntentRes.pending ? (
        <LoadingSpinner type="circular" />
      ) : (
        <form onSubmit={handleSubmit(submitForm)}>
          <div className="card-outline">
            <FormRow>
              <StyledLabel>
                Card number
                <CardNumberElement
                  options={{
                    ...stripeElementOptions,
                    showIcon: true
                  }}
                />
              </StyledLabel>
            </FormRow>

            <StyledFormRow>
              <StyledLabel>
                Expiration date
                <CardExpiryElement
                  options={stripeElementOptions}
                />
              </StyledLabel>

              <StyledLabel>
                CVC
                <CardCvcElement
                  options={stripeElementOptions}
                />
              </StyledLabel>
            </StyledFormRow>
          </div>

          <FormRow>
            <Input
              ref={register({ required: "Please provide the cardholer's name" })}
              as="input"
              name="name"
              label="Cardholder name"
              autoComplete="cc-name"
              error={errors.name?.message}
            />
          </FormRow>

          <FormRow>
            <Select
              as="select"
              name="country"
              label="Country"
              options={countryList}
            />
          </FormRow>

          <FormRow>
            <Input
              ref={register()}
              as="input"
              name="addressLine1"
              label="Address line 1"
              autoComplete="address-line1"
            />
          </FormRow>

          <FormRow>
            <Input
              ref={register()}
              as="input"
              name="addressLine2"
              label="Address line 2"
              autoComplete="address-line2"
            />
          </FormRow>

          <StyledFormRow>
            <Input
              ref={register()}
              as="input"
              name="city"
              label="Town or city"
            />

            <Input
              ref={register({ required: "Please provide a valid postcode" })}
              as="input"
              name="postcode"
              label="Postcode"
              autoComplete="postal-code"
              error={errors.postcode?.message}
            />
          </StyledFormRow>

          <FormRow>
            <div className="check-box-field">
              <CheckboxStyled
                ref={register()}
                name="allowOffSessionPayments"
              />

              <Text
                as="p"
                fontSize="h4"
              >
                {"Allow payments to be taken automatically "}

                <HelperText
                  className="off-session-helper-text"
                  tipPlace="top"
                  tippyId="off-session-payments"
                  helpText="Off Sesion."
                  helpExplanation="Off session payments happen in the background. We attempt to charge your card if you have not already paid for the order by the cutoff time. Using a payment method that allows off session payments will help you never miss an order."
                />
              </Text>

            </div>
          </FormRow>

          <FormRow>
            <div className="check-box-field">
              <CheckboxStyled
                ref={register()}
                name="setAsDefaultCard"
              />

              <Text
                as="p"
                fontSize="h4"
              >
                {`Set as default card${totalDrops && totalDrops <= 1 ? "." : ` for all ${totalDrops} ${terminology?.drop.plural.text}.`}`}
              </Text>
            </div>
          </FormRow>

          <Text
            className="pound-charge"
            fontSize="h5"
            weight="bold"
          >
            By pressing add card, you authorise a transaction of £1 to be taken against the card for validation. You will then be refunded in full within 5 working days.
          </Text>

          <FormRow>
            <Button
              type="submit"
              size="medium"
              loading={confirmCardSetupLoading}
            >
              Add card
            </Button>
          </FormRow>
        </form>
      )}
    </StyledStripeElementsSetupCardForm>
  );
};

export default StripeElementsSetupCardForm;

const StyledStripeElementsSetupCardForm = styled.div`
  ${({ theme }) => css`
    position: relative;
    padding-right: 1rem;
    min-height: 24rem;
    overflow: hidden;
    -webkit-overflow-scrolling: touch;

    .card-outline {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      margin: 0 auto;
      padding: 2.4rem;
      max-width: 30rem;
      height: 18rem;
      background: linear-gradient(120deg,#f9f9f9 0%,#c4d0d4 100%);
      border-radius: 1.4rem;
    }

    .pound-charge {
      display: block;
      margin-top: ${theme.spacing.margins.sm};
    }

    .check-box-field {
      display: flex;
      
      p {
        display: flex;
        flex-wrap: wrap;
        align-content: center;
        margin: 0;
        margin-left: ${theme.spacing.margins.sm};
      }
    }

    .off-session-helper-text {
      margin-left: ${theme.spacing.margins.xs};
    }
  `}
`;

const StyledLabel = styled(Text).attrs(() => ({ as: "label" }))`
  ${({ theme }) => css`
    display: block;
    width: 100%;
    font-size: ${theme.fonts.sizes.h4};

    .StripeElement {
      padding-top: 0.4rem;
    }
  `};
`;

const StyledFormRow = styled(FormRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 40rem;

  > * {
    width: 46%;
  }
`;
